import React, { useEffect, useMemo, useRef, useState } from 'react';
import PaymentModal from 'src/components/PaymentModal';
import { CarrierLogo } from 'src/components/RatePicker';
import loadsureImage from 'src/images/loadsure.png';
import shipengineImage from 'src/images/shipengine.png';
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  TabContent,
  TabPane,
  toast,
  useApp,
  useScreenSize
} from 'straightline-ui';
import { formatMoney } from 'straightline-utils/accounting';
import {
  ACCESSORIAL_CODES,
  isLTL,
  LOADSURE,
  MARKETS,
  PACKAGE_TYPES,
  PROVIDER_TYPE,
  STATUS
} from 'straightline-utils/constants';
import { formatDate } from 'straightline-utils/time';

const { ZIPS } = MARKETS;

const useInitialValues = (quote, rate, copiedQuoteId) => {
  const app = useApp();

  const initialValues = {
    rate_id: rate.id,
    notes: undefined,
    reference_number: quote.reference_number || undefined,
    pickup_address: {
      name: quote.pickup_address.name || undefined,
      contact: quote.pickup_address.contact || undefined,
      phone: quote.pickup_address.phone || undefined,
      email: quote.pickup_address.email || undefined,
      appointment: quote.pickup_address.appointment || undefined
    },
    delivery_address: {
      name: quote.delivery_address.name || undefined,
      contact: quote.delivery_address.contact || undefined,
      phone: quote.delivery_address.phone || undefined,
      email: quote.delivery_address.email || undefined,
      appointment: quote.delivery_address.appointment || undefined
    }
  };

  const { result: shipment } = app.service('api/shipments').useFindOne(
    {
      query: {
        shipment_quote_id: copiedQuoteId,
        $select: ['notes', 'reference_number']
      }
    },
    {
      keepPreviousData: true,
      enabled: !!copiedQuoteId
    }
  );

  if (shipment) {
    Object.assign(initialValues, {
      notes: shipment.notes
    });
  }

  return initialValues;
};

const AddressCard = ({ address, date, accessorials, type, className = '' }) => {
  const codes = ACCESSORIAL_CODES[type.toUpperCase()].filter(({ value }) => {
    return (accessorials || []).indexOf(value) > -1;
  });
  return (
    <div className={`border rounded p-3 bg-lighter ${className}`}>
      <div className="d-flex">
        {/* <div style={{ width: 100 }}>
          <MapImage
            className="mw-100 rounded"
            size="100x100"
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
          >
            <MarkerImage
              location={{
                lat: address.latitude,
                lng: address.longitude
              }}
              color="red"
              label={type === 'pickup' ? 'A' : 'B'}
            />
          </MapImage>
        </div> */}
        <div>
          {address.name && (
            <div className="font-weight-bold">{address.name}</div>
          )}
          <div>{address.address_1}</div>
          <div>{`${address.city}, ${address.state} ${address.postal_code}`}</div>
          <div>{formatDate(date, 'iiii, LLLL d, yyyy')}</div>
          {codes.length > 0 && (
            <div className="mt-1">
              {codes.map(({ label, value }) => {
                return (
                  <Badge
                    pill
                    color="primary"
                    className="mr-2"
                    style={{ opacity: 0.65 }}
                    key={value}
                  >
                    {label}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function BookShipmentModal({
  onSuccess,
  onCancel,
  quote,
  copiedQuoteId,
  rate,
  open
}) {
  const { sm } = useScreenSize();

  return (
    <Modal
      isOpen={open}
      style={{ maxWidth: sm ? 1000 : '100%' }}
      // size="xl"
      toggle={onCancel}
      contentClassName="h-100"
      modalClassName="modal-fullscreen-md"
      scrollable
      centered
    >
      {() => (
        <ModalContent
          quote={quote}
          rate={rate}
          onSuccess={onSuccess}
          onCancel={onCancel}
          copiedQuoteId={copiedQuoteId}
        />
      )}
    </Modal>
  );
}

function ModalContent({
  quote: initialQuote,
  rate,
  copiedQuoteId,
  onSuccess,
  onCancel
}) {
  const app = useApp();
  const [tab, setTab] = useState('details');

  const { result: quote } = app
    .service('api/shipment-quotes')
    .useGet(initialQuote.id, null, {
      placeholderData: initialQuote,
      keepPreviousData: true
    });

  const offer = quote.offers?.find((offer) => offer.rate_id === rate.id);

  const pickupMarket = useMemo(
    () =>
      ZIPS.find((market) =>
        quote.pickup_address.postal_code.startsWith(market.zip3)
      ),
    [quote.pickup_address.postal_code]
  );

  const deliveryMarket = useMemo(
    () =>
      ZIPS.find((market) =>
        quote.delivery_address.postal_code.startsWith(market.zip3)
      ),
    [quote.delivery_address.postal_code]
  );

  const { result: shipments } = app.service('api/shipments').useFind(
    {
      query: {
        $limit: 100,
        $select: [
          'id',
          'rate',
          'carrier_id',
          'load_weight',
          'travel',
          'line_items'
        ],
        $sort: { pickup_date: -1 },
        carrier_id: { $ne: null },
        status_type: { $ne: STATUS.CANCELLED },
        mode: quote.mode,
        truck_type: quote.truck_type,
        'pickup_address.postal_code': {
          $like: `${
            pickupMarket?.zip3 || quote.pickup_address.postal_code.slice(0, 3)
          }%`
        },
        'delivery_address.postal_code': {
          $like: `${
            deliveryMarket?.zip3 ||
            quote.delivery_address.postal_code.slice(0, 3)
          }%`
        }
      }
    },
    {
      keepPreviousData: true
    }
  );

  const carriers = shipments
    ? shipments.data.reduce((acc, shipment) => {
        if (shipment.carrier) {
          if (!acc[shipment.carrier.id]) {
            acc[shipment.carrier.id] = {
              ...shipment.carrier,
              shipments: [shipment]
            };
          } else {
            acc[shipment.carrier.id].shipments.push(shipment);
          }
        }
        return acc;
      }, {})
    : {};

  return (
    <TabContent activeTab={tab} className="h-100">
      <TabPane tabId="offer" className="h-100">
        <OfferTab
          quote={quote}
          rate={rate}
          offer={offer}
          setTab={setTab}
          shipments={shipments}
        />
      </TabPane>
      <TabPane tabId="details" className="h-100">
        <DetailsTab
          quote={quote}
          rate={rate}
          offer={offer}
          setTab={setTab}
          shipments={shipments}
          carriers={carriers}
          onCancel={onCancel}
        />
      </TabPane>
      <TabPane tabId="book" className="h-100">
        <BookTab
          quote={quote}
          rate={rate}
          offer={offer}
          onSuccess={onSuccess}
          setTab={setTab}
          copiedQuoteId={copiedQuoteId}
        />
      </TabPane>
      <TabPane tabId="insurance" className="h-100">
        <InsuranceTab quote={quote} rate={rate} offer={offer} setTab={setTab} />
      </TabPane>
    </TabContent>
  );
}

function ModalBanner({ children, className = '' }) {
  return (
    <div
      className={`p-3 p-md-4 mt-n3 mt-md-n4 ml-n3 ml-md-n4 mr-n3 mr-md-n4 bg-lighter ${className}`}
    >
      {children}
    </div>
  );
}

function BookTab({ quote, rate, offer, onSuccess, setTab, copiedQuoteId }) {
  const app = useApp();
  const { sm } = useScreenSize();
  const initialValues = useInitialValues(quote, rate, copiedQuoteId);
  const schema = app.service('api/shipment-quotes').acceptSchema;
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const {
    pickup_address,
    delivery_address,
    pickup_date,
    due_date,
    accessorial_codes
  } = quote;

  function handleAccept(data, formik) {
    formik.setSubmitting(true);
    app
      .service('api/shipment-quotes')
      .accept(quote.id, {
        rate_id: rate.id,
        ...data
      })
      .then((shipment) => {
        toast(
          <div className="p-3">
            <p className="text-primary lead font-weight-bold">
              Shipment Booked!
            </p>
            <span className="lead">
              Your shipment is now waiting to be matched and you will receive a
              notification when it is!
            </span>
          </div>,
          { hideProgressBar: true }
        );
        formik.setSubmitting(false);
        if (onSuccess) {
          onSuccess(shipment);
        }
      })
      .catch((error) => {
        if (error.code === 402) {
          setIsAddingPayment(true);
        } else {
          formik.setSubmitting(false);
          toast.errors(error);
          if (error.formik) {
            formik.setErrors(error.formik);
          }
        }
      });
  }

  return (
    <>
      <Formik
        onSubmit={handleAccept}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        render={({ values, isSubmitting, submitForm }) => {
          return (
            <Form className="modal-content h-100">
              <Header quote={quote} rate={rate} offer={offer} />
              <ModalBody className="p-3 p-md-4 h-100">
                <ModalBanner className="border-bottom">
                  You're almost there! To book this shipment, tell us some more
                  details about the pickup and delivery. Then click "Book
                  Shipment". Once booked, you will begin receiving updates for
                  status changes, ETA's and locations.
                </ModalBanner>
                {sm && (
                  <Row>
                    <Col
                      sm="6"
                      className={`pr-sm-4 pt-4 ${sm ? 'border-right' : ''}`}
                    >
                      <AddressCard
                        address={{
                          ...pickup_address,
                          ...values.pickup_address
                        }}
                        date={pickup_date}
                        type="pickup"
                        accessorials={accessorial_codes}
                        className="h-100"
                      />
                    </Col>
                    <Col sm="6" className="pl-sm-4 pt-4">
                      <AddressCard
                        address={{
                          ...delivery_address,
                          ...values.delivery_address
                        }}
                        date={due_date}
                        type="delivery"
                        accessorials={accessorial_codes}
                        className="h-100"
                      />
                    </Col>
                  </Row>
                )}
                <Row className="mb-4">
                  <Col
                    sm="6"
                    className={`pr-sm-4 pt-4 ${sm ? 'border-right' : ''}`}
                  >
                    {/* <h4>Pickup</h4> */}
                    {!sm && (
                      <div className="mb-4">
                        <AddressCard
                          address={{
                            ...pickup_address,
                            ...values.pickup_address
                          }}
                          date={pickup_date}
                          type="pickup"
                          accessorials={accessorial_codes}
                        />
                      </div>
                    )}
                    <FormGroup className="position-relative">
                      <Label>Company*</Label>
                      <FormInput
                        name="pickup_address.name"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="pickup_address.name"
                        className="position-absolute"
                      />
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label>Pickup Appointment*</Label>
                      <FormInput
                        name="pickup_address.appointment"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="pickup_address.appointment"
                        className="position-absolute"
                      />
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label>Pickup Contact*</Label>
                      <FormInput
                        name="pickup_address.contact"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="pickup_address.contact"
                        className="position-absolute"
                      />
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label>Pickup Email</Label>
                      <FormInput
                        name="pickup_address.email"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="pickup_address.email"
                        className="position-absolute"
                      />
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label>Pickup Phone</Label>
                      <FormInput
                        name="pickup_address.phone"
                        type="phone"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="pickup_address.phone"
                        className="position-absolute"
                      />
                    </FormGroup>
                  </Col>
                  <Col sm="6" className="pl-sm-4 pt-4">
                    {/* <h4>Delivery</h4> */}
                    {!sm && (
                      <div className="mb-4">
                        <AddressCard
                          address={{
                            ...delivery_address,
                            ...values.delivery_address
                          }}
                          date={due_date}
                          type="delivery"
                          accessorials={accessorial_codes}
                        />
                      </div>
                    )}
                    <FormGroup className="position-relative">
                      <Label>Company*</Label>
                      <FormInput
                        name="delivery_address.name"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="delivery_address.name"
                        className="position-absolute"
                      />
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label>Delivery Appointment*</Label>
                      <FormInput
                        name="delivery_address.appointment"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="delivery_address.appointment"
                        className="position-absolute"
                      />
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label>Delivery Contact*</Label>
                      <FormInput
                        name="delivery_address.contact"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="delivery_address.contact"
                        className="position-absolute"
                      />
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label>Delivery Email</Label>
                      <FormInput
                        name="delivery_address.email"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="delivery_address.email"
                        className="position-absolute"
                      />
                    </FormGroup>
                    <FormGroup className="position-relative">
                      <Label>Delivery Phone</Label>
                      <FormInput
                        name="delivery_address.phone"
                        type="phone"
                        disabled={isSubmitting}
                      />
                      <FormFeedback
                        name="delivery_address.phone"
                        className="position-absolute"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <ModalBanner className="border-bottom border-top mb-4">
                  Next, assign a reference number to this shipment. You can use
                  your own PO Number or any other identifier that your company
                  may use. This reference number will be available to you on
                  BOL's, labels, and other documents that Straightline may
                  provide. You can also provide notes that will be passed along
                  to the carrier.
                </ModalBanner>
                <FormGroup className="position-relative">
                  <Label>Reference Number</Label>
                  <FormInput
                    type="text"
                    name="reference_number"
                    placeholder="PO number, RFQ number, or any other internal reference"
                    disabled={isSubmitting}
                  />
                  <FormFeedback
                    name="reference_number"
                    className="position-absolute"
                  />
                </FormGroup>
                <FormGroup className="position-relative">
                  <Label>Notes</Label>
                  <FormInput
                    type="textarea"
                    name="notes"
                    rows="3"
                    placeholder="Please include any special instructions that a driver would need to know in order to deliver this shipment."
                    disabled={isSubmitting}
                  />
                  <FormFeedback name="notes" className="position-absolute" />
                </FormGroup>
                <div className="text-muted">
                  This rate includes line-haul & fuel surcharge. All delivery
                  dates and times are estimates. For a list of all possible
                  accessorial charges, refer to the{' '}
                  <a href="/support" target="_blank" rel="noopener noreferrer">
                    support
                  </a>{' '}
                  page.
                </div>
              </ModalBody>
              <ModalFooter className="p-3 px-md-4">
                <div className="d-flex justify-content-between w-100 m-0">
                  <div>
                    <Button
                      color="white"
                      type="button"
                      className=""
                      onClick={() => setTab('details')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="text-right">
                    {/* <Button
                      type="button"
                      color="primary"
                      className="mr-3 "
                      onClick={() => setTab('offer')}
                      disabled={isSubmitting || offer}
                    >
                      <Icon type="dollar-sign" className="mr-2" />
                      {sm && 'Make '}Offer
                    </Button> */}
                    <Button
                      type="submit"
                      color="primary"
                      className={`${isSubmitting ? 'is-loading' : ''}`}
                      disabled={isSubmitting}
                    >
                      <Icon type="package" className="mr-2" />
                      Book Shipment
                    </Button>
                  </div>
                </div>
              </ModalFooter>
              <PaymentModal
                open={isAddingPayment}
                title="To request a truck, please add a payment method."
                onSuccess={() => {
                  setIsAddingPayment(false);
                  submitForm();
                }}
                onCancel={() => setIsAddingPayment(false)}
                onError={(error) => {
                  setIsAddingPayment(false);
                  toast.error(
                    `There was an issue linking your payment method: ${error.message}`
                  );
                }}
              />
            </Form>
          );
        }}
      />
    </>
  );
}

function DetailsTab({
  quote,
  rate,
  offer,
  setTab,
  shipments,
  carriers,
  onCancel
}) {
  const { sm } = useScreenSize();
  const units = quote.line_items.reduce((acc, item) => {
    return acc + item.units;
  }, 0);

  let insuranceCharge = 0;
  if (rate.insurance) {
    insuranceCharge =
      (rate.insurance.premium_amount || 0) +
      (rate.insurance.service_fee_amount || 0) +
      (rate.insurance.tax_amount || 0);
  }

  const price = offer?.accepted_at ? offer.amount : rate.price.shipper_price;

  const totalPrice = price + insuranceCharge;

  const lineItemType = quote.line_items[0].type;
  const packageType = PACKAGE_TYPES.find((pkg) => {
    return pkg.value === lineItemType;
  });

  const canOffer =
    !quote.accessorial_codes?.includes('HAZM') &&
    !quote.accessorial_codes?.includes('TANK');

  return (
    <div className="modal-content h-100">
      <Header quote={quote} rate={rate} offer={offer} />
      <ModalBody className="p-3 p-md-4 h-100">
        <ModalBanner className="border-bottom mb-4">
          This rate includes line-haul & fuel surcharge. All delivery dates and
          times are estimates. For a list of all possible accessorial charges,
          refer to the{' '}
          <a href="/support" target="_blank" rel="noopener noreferrer">
            support
          </a>{' '}
          page.
          {canOffer && (
            <>
              Don't like this price? You can also click "Make Offer" to make a
              counter offer.
            </>
          )}
        </ModalBanner>
        <Row>
          <Col sm="6">
            <CarrierDetail rate={rate} carriers={carriers} />
          </Col>
          <Col sm="6">
            <div className="border rounded p-3">
              <div className="d-flex justify-content-between align-items-start">
                <div className="pr-3">
                  <div>Service</div>
                </div>
                <div>{rate.service_level.name}</div>
              </div>
              <div className="d-flex justify-content-between align-items-start mb-1">
                <div className="pr-3">
                  <div>Transit</div>
                </div>
                <div>{rate.service_level.description}</div>
              </div>
              {isLTL(quote.mode) && (
                <div className="d-flex justify-content-between align-items-start">
                  <div className="pr-3">
                    <div>Lane</div>
                  </div>
                  <div>{rate.service_level.lane}</div>
                </div>
              )}
              <hr />
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>Cost per Mile</div>
                <div>
                  {offer?.accepted_at ? (
                    <div className="d-flex">
                      <del className="mr-2 text-secondary">
                        {formatMoney(
                          rate.price.shipper_price / quote.travel.miles
                        )}
                      </del>
                      <div>
                        {formatMoney(offer.amount / quote.travel.miles)}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {formatMoney(
                        rate.price.shipper_price / quote.travel.miles
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>Cost per Pound</div>
                <div>
                  {offer?.accepted_at ? (
                    <div className="d-flex">
                      <del className="mr-2 text-secondary">
                        {formatMoney(
                          rate.price.shipper_price / quote.load_weight
                        )}
                      </del>
                      <div>{formatMoney(offer.amount / quote.load_weight)}</div>
                    </div>
                  ) : (
                    <div>
                      {formatMoney(
                        rate.price.shipper_price / quote.load_weight
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>Cost per {packageType.label}</div>
                <div>
                  {offer?.accepted_at ? (
                    <div className="d-flex">
                      <del className="mr-2 text-secondary">
                        {formatMoney(rate.price.shipper_price / units)}
                      </del>
                      <div>{formatMoney(offer.amount / units)}</div>
                    </div>
                  ) : (
                    <div>{formatMoney(rate.price.shipper_price / units)}</div>
                  )}
                </div>
              </div>
              {rate.insurance && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="small text-secondary">
                    values do not include insurance charges
                  </div>
                </div>
              )}
              {rate.insurance && (
                <>
                  <hr />
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div>Coverage</div>
                    <div>{formatMoney(rate.insurance.limit_amount || 0)}</div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div>Deductible</div>
                    <div>
                      {formatMoney(rate.insurance.deductible_amount || 0)}
                    </div>
                  </div>
                </>
              )}
              <hr />
              {rate.insurance && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>Insurance</div>
                  <div>{formatMoney(insuranceCharge)}</div>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div className="pr-3">
                  <div>Freight Charge</div>
                </div>
                {offer?.accepted_at ? (
                  <div className="d-flex">
                    <del className="mr-2 text-secondary">
                      {formatMoney(rate.price.shipper_price)}
                    </del>
                    <div>{formatMoney(offer.amount)}</div>
                  </div>
                ) : (
                  <div>{formatMoney(rate.price.shipper_price)}</div>
                )}
              </div>
              <div className="d-flex justify-content-between align-items-start font-weight-bold h3 mb-0">
                <div className="pr-3">
                  <div>Total Price</div>
                </div>
                <div>{formatMoney(totalPrice)}</div>
              </div>
            </div>
            {offer && (
              <div className="mt-3">
                <PriceOfferAlert offer={offer} />
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter className="p-3 px-md-4">
        <div className="d-flex justify-content-between w-100 m-0">
          <div>
            <Button type="button" color="white" className="" onClick={onCancel}>
              Close
            </Button>
          </div>
          <div>
            {canOffer && (
              <Button
                type="button"
                color="white"
                className="mr-3"
                onClick={() => setTab('offer')}
                disabled={offer}
              >
                <Icon type="dollar-sign" className="mr-2" />
                {sm && 'Make '}Offer
              </Button>
            )}
            {rate.insurance ? (
              <Button
                type="button"
                color="primary"
                className=""
                onClick={() => setTab('insurance')}
              >
                <Icon type="shield" className="mr-2" />
                {sm && 'View '}Insurance
              </Button>
            ) : (
              <Button
                type="button"
                color="primary"
                className=""
                onClick={() => setTab('book')}
              >
                <Icon type="check-circle" className="mr-2" />
                Select{sm && ' Rate'}
              </Button>
            )}
          </div>
        </div>
      </ModalFooter>
    </div>
  );
}

function CarrierDetail({ rate, carriers }) {
  if (rate.carrier) {
    const { carrier } = rate;
    const validMC =
      carrier.mc_number &&
      carrier.mc_number.length &&
      carrier.mc_number !== '00000000';

    return (
      <div className="d-flex align-items-top mb-4">
        <div className="mr-3 d-none d-md-block">
          <Icon type="truck" className="h2 mb-0" style={{ lineHeight: 1 }} />
        </div>
        <div className="flex-grow-1">
          <div className="h2 mb-3">{carrier.name}</div>
          <div className="mb-4">
            Leverage Straightline's discounted rates with{' '}
            <span className="font-weight-bold">{carrier.name}</span>.
            Straightline automatically dispatches rates, creates appointments,
            tracks shipments, and provides real-time updates.
          </div>
          <div className="border rounded p-3">
            <div className="d-flex align-items-center">
              <div className="mr-3">
                <CarrierLogo carrier={carrier} size={35} />
              </div>
              <div className="flex-grow-1">
                <div style={{ lineHeight: 1 }}>{carrier.name}</div>
                <div className="text-secondary">{rate.service_level.name}</div>
              </div>
            </div>
            <hr />
            {validMC && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>MC Number</div>
                  <div>{carrier.mc_number}</div>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>DOT Number</div>
                  <div>{carrier.dot_number}</div>
                </div>
                {carrier.scac_code && (
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div>SCAC Code</div>
                    <div>{carrier.scac_code}</div>
                  </div>
                )}
                <hr />
              </>
            )}
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div>Recent Shipments</div>
              <div>{carriers[carrier.id]?.shipments.length || 0}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const previousCarriers = Object.values(carriers)
    .sort((a, b) => {
      return b.shipments.length - a.shipments.length;
    })
    .slice(0, 3);

  return (
    <div className="d-flex align-items-top mb-4">
      <div>
        <Icon type="truck" className="h2 mb-0" style={{ lineHeight: 1 }} />
      </div>
      <div className="flex-grow-1 pl-3">
        <div className="h2 mb-3">Straightline Carrier Network</div>
        <div>
          Straightline will use our network of carriers to transport this
          shipment. Once assigned to a carrier, you will be updated with the
          carrier's legal information and driver contact.
        </div>
        {previousCarriers.length > 0 && (
          <div className="mt-3">
            <Label>Previous Carriers</Label>
            <ListGroup>
              {previousCarriers.map((carrier) => {
                const validMC =
                  carrier.mc_number &&
                  carrier.mc_number.length &&
                  carrier.mc_number !== '00000000';

                return (
                  <ListGroupItem key={carrier.id} className="p-3">
                    <div className="d-flex">
                      <div className="mr-3">
                        <CarrierLogo carrier={carrier} />
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>{carrier.name}</div>
                          <div className="d-flex align-items-center">
                            <Icon
                              type="package"
                              className="mr-2 text-secondary"
                            />
                            {carrier.shipments.length}
                          </div>
                        </div>
                        {validMC && (
                          <div className="d-flex align-items-center small text-secondary">
                            <div>MC:{carrier.mc_number}</div>
                            <div className="ml-2">DOT:{carrier.dot_number}</div>
                            {carrier.scac_code && (
                              <div className="ml-2">
                                SCAC:{carrier.scac_code}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </ListGroupItem>
                );
              })}
            </ListGroup>
          </div>
        )}
      </div>
    </div>
  );
}

function getAverages(shipments) {
  if (!shipments) {
    return {
      shipper_price: 0,
      cost_per_mile: 0,
      cost_per_pound: 0,
      cost_per_unit: 0
    };
  }
  const totals = shipments.reduce(
    (totals, shipment) => {
      totals.shipper_price += shipment.rate.price.shipper_price;
      totals.load_weight += shipment.load_weight;
      totals.miles += Number(shipment.travel.miles);
      totals.units += shipment.line_items.reduce((acc, item) => {
        return acc + item.units;
      }, 0);
      return totals;
    },
    {
      shipper_price: 0,
      load_weight: 0,
      miles: 0,
      units: 0
    }
  );
  return {
    shipper_price: totals.shipper_price / shipments.length,
    cost_per_mile: totals.shipper_price / totals.miles,
    cost_per_pound: totals.shipper_price / totals.load_weight,
    cost_per_unit: totals.shipper_price / totals.units
  };
}

function OfferTab({ quote, rate, offer, setTab, shipments }) {
  const app = useApp();
  const inputRef = useRef(null);
  const validationSchema = app.service('api/shipment-quote-offers').schema;

  useEffect(() => {
    inputRef?.current?.focus();
  }, []);

  const onSubmit = (data, formik) => {
    app
      .service('api/shipment-quote-offers')
      .create(data)
      .then((offer) => {
        formik.setSubmitting(false);
        app.service('api/shipment-quotes').invalidateGet(quote.id);
        setTab('details');
        if (offer.accepted_at) {
          toast(
            <div className="p-3">
              <h2 className="text-primary">
                <Icon type="dollar-sign" className="mr-2" />
                Offer Accepted!
              </h2>
              <div className="lead text-secondary mb-0">
                Your offer has been accepted! You can now book this shipment for{' '}
                {formatMoney(offer.amount)}.
              </div>
            </div>,
            { autoClose: 5000, hideProgressBar: true }
          );
        } else {
          toast(
            <div className="p-3">
              <h2 className="text-dark">
                <Icon type="dollar-sign" className="mr-2" />
                Offer Submitted!
              </h2>
              <div className="lead text-secondary mb-0">
                We are reviewing your offer and will get back to you ASAP!
              </div>
            </div>,
            { autoClose: 5000, hideProgressBar: true }
          );
        }
      })
      .catch((error) => {
        toast.errors(error);
        if (error.formik) {
          formik.setErrors(error.formik);
        }
        formik.setSubmitting(false);
      });
  };

  const averages = getAverages(shipments?.data);

  const units = quote.line_items.reduce((acc, item) => {
    return acc + item.units;
  }, 0);

  const lineItemType = quote.line_items[0].type;
  const packageType = PACKAGE_TYPES.find((pkg) => {
    return pkg.value === lineItemType;
  });

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={{
        amount: undefined,
        notes: undefined,
        rate_id: rate.id,
        shipment_quote_id: quote.id
      }}
      validationSchema={validationSchema}
      render={({ isSubmitting, values }) => {
        return (
          <Form className="modal-content h-100">
            <Header quote={quote} rate={rate} offer={offer} />
            <ModalBody className="p-3 p-md-4 h-100">
              <Row className="mb-n4">
                <Col sm="6" className="mb-4">
                  <Label>Current Price</Label>
                  <div className="mb-4 border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div>Cost per Mile</div>
                      <div>
                        {formatMoney(
                          rate.price.shipper_price / quote.travel.miles
                        )}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div>Cost per Pound</div>
                      <div>
                        {formatMoney(
                          rate.price.shipper_price / quote.load_weight
                        )}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>Cost per {packageType.label}</div>
                      <div>{formatMoney(rate.price.shipper_price / units)}</div>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between align-items-center">
                      <div>Freight Charge</div>
                      <div>{formatMoney(rate.price.shipper_price)}</div>
                    </div>
                  </div>
                  {shipments?.data.length > 0 && (
                    <>
                      <div className="d-flex justify-content-between">
                        <Label>Historical Average</Label>
                      </div>
                      <div className="border rounded p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div>Cost per Mile</div>
                          <div>{formatMoney(averages.cost_per_mile)}</div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div>Cost per Pound</div>
                          <div>{formatMoney(averages.cost_per_pound)}</div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>Cost per {packageType.label}</div>
                          <div>{formatMoney(averages.cost_per_unit)}</div>
                        </div>
                        <hr />
                        <div className="d-flex justify-content-between align-items-center">
                          <div>Freight Charge</div>
                          <div>{formatMoney(averages.shipper_price)}</div>
                        </div>
                      </div>
                      <div className="text-muted small">
                        Historical averages are based on the last{' '}
                        {shipments.data.length} shipment
                        {shipments.data.length === 1 ? '' : 's'} that match this
                        lane.
                      </div>
                    </>
                  )}
                  {shipments?.data?.length === 0 && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between">
                        <Label>Historical Average</Label>
                      </div>
                      <div className="border rounded p-3 text-secondary">
                        There are no previously booked shipments matching this
                        lane. As you book more loads, you will see a comparison
                        of rates.
                      </div>
                    </div>
                  )}
                </Col>
                <Col sm="6">
                  <Label>Offer Amount</Label>
                  <div className="mb-4 border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div>Cost per Mile</div>
                      <div>
                        {values.amount ? (
                          <span className="animated fadeIn">
                            {formatMoney(values.amount / quote.travel.miles)}
                          </span>
                        ) : (
                          <span className="text-secondary">$0.00</span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div>Cost per Pound</div>
                      <div>
                        {values.amount ? (
                          <span className="animated fadeIn">
                            {formatMoney(values.amount / quote.load_weight)}
                          </span>
                        ) : (
                          <span className="text-secondary">$0.00</span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>Cost per {packageType.label}</div>
                      <div>
                        {values.amount ? (
                          <span className="animated fadeIn">
                            {formatMoney(values.amount / units)}
                          </span>
                        ) : (
                          <span className="text-secondary">$0.00</span>
                        )}
                      </div>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between align-items-center">
                      <div>Offer Amount</div>
                      <div style={{ maxWidth: 125 }}>
                        <InputGroup className="input-group-merge">
                          <FormInput
                            type="money"
                            name="amount"
                            className="form-control-prepended rounded-right"
                            disabled={isSubmitting}
                            innerRef={inputRef}
                          />
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <Icon type="dollar-sign" />
                            </InputGroupText>
                          </InputGroupAddon>
                          {/* <InputGroupAddon addonType="append">
                            <InputGroupText></InputGroupText>
                          </InputGroupAddon> */}
                        </InputGroup>
                      </div>
                    </div>
                  </div>
                  {rate.insurance && (
                    <div className="text-primary small mt-n3 mb-4">
                      Do not include insurance in your offer. Insurance is an
                      additional charge and will be added to your final invoice.
                    </div>
                  )}
                  <FormGroup>
                    <Label>Offer Notes</Label>
                    <FormInput
                      type="textarea"
                      rows="4"
                      name="notes"
                      disabled={isSubmitting}
                    />
                    <FormFeedback name="notes" />
                  </FormGroup>
                  <div className="">
                    You may make one counteroffer to this rate. Some
                    counteroffers are automatically accepted. Others will be
                    reviewed by Straightline. If your offer is accepted, you can
                    then book this rate for your offered amount.
                  </div>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter className="p-3 px-md-4">
              <div className="d-flex justify-content-between w-100 m-0">
                <Button
                  type="button"
                  color="white"
                  disabled={isSubmitting}
                  onClick={() => setTab('details')}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  <Icon type="dollar-sign" className="mr-2" />
                  Submit Offer
                </Button>
              </div>
            </ModalFooter>
          </Form>
        );
      }}
    />
  );
}

const defaultExclusion =
  'Certain types of items, like currency or perishables , cannot be insured. Some other products, such as cell phones, televisions, and laptop computers, can only be insured under certain conditions.';

function InsuranceTab({ quote, rate, offer, setTab }) {
  if (!rate.insurance) {
    return (
      <div className="modal-content h-100">
        <Header quote={quote} rate={rate} offer={offer} />
        <ModalBody className="p-3 p-md-4 h-100">
          <ModalBanner className="border-bottom">
            This rate does not include insurance. If you would like to add
            insurance to this shipment, please repeat the quote and add a
            commodity and insured value.
          </ModalBanner>
        </ModalBody>
        <ModalFooter className="p-3 px-md-4">
          <div className="d-flex justify-content-between w-100 m-0">
            <div>
              <Button
                color="white"
                onClick={() => setTab('details')}
                className="mr-2"
              >
                Cancel
              </Button>
            </div>
          </div>
        </ModalFooter>
      </div>
    );
  }
  const insuranceCharge =
    (rate.insurance.tax_amount || 0) +
    (rate.insurance.service_fee_amount || 0) +
    (rate.insurance.premium_amount || 0);

  return (
    <div className="modal-content h-100">
      <Header quote={quote} rate={rate} offer={offer} />
      <ModalBody className="p-3 p-md-4 h-100">
        <ModalBanner className="border-bottom mb-4">
          Please verify the following details before booking this shipment. You
          must check the box confirming that you agree to all Terms, Conditions,
          & Exclusions before continuing.
        </ModalBanner>
        <Row>
          <Col sm="6">
            <div className="d-flex align-items-top mb-4">
              <div className="mr-3 d-none d-md-block">
                <Icon
                  type="shield"
                  className="h2 mb-0"
                  style={{ lineHeight: 1 }}
                />
              </div>
              <div className="flex-grow-1">
                <div className="h2 mb-2">{rate.insurance.name}</div>
                <div>{rate.insurance.description}</div>
                {rate.insurance.provider_type === PROVIDER_TYPE.LOADSURE && (
                  <div className="mt-3">
                    <div className="text-secondary">Insurance provided by</div>
                    <div className="mt-2">
                      <a
                        target="_blank"
                        href="https://www.loadsure.net"
                        rel="noreferrer"
                      >
                        <div style={{ maxWidth: 110, display: 'inline-block' }}>
                          <img
                            src={loadsureImage}
                            className="img-fluid"
                            alt="Loadsure Logo"
                          />
                        </div>
                      </a>
                    </div>
                  </div>
                )}
                {rate.insurance.provider_type === PROVIDER_TYPE.SHIPENGINE && (
                  <div className="mt-3">
                    <div className="text-secondary">Insurance provided by</div>
                    <div className="mt-2">
                      <a
                        target="_blank"
                        href="https://www.shipengine.com/"
                        rel="noreferrer"
                      >
                        <div style={{ maxWidth: 150, display: 'inline-block' }}>
                          <img
                            src={shipengineImage}
                            className="img-fluid"
                            alt="Loadsure Logo"
                          />
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Col>
          <Col sm="6">
            <div className="border rounded p-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>Limit</div>
                <div>{formatMoney(rate.insurance.limit_amount)}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div>Deductible</div>
                <div>{formatMoney(rate.insurance.deductible_amount || 0)}</div>
              </div>
              <hr />
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>Premium</div>
                <div>{formatMoney(rate.insurance.premium_amount)}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>Taxes & Fees</div>
                <div>
                  {formatMoney(
                    (rate.insurance.tax_amount || 0) +
                      (rate.insurance.service_fee_amount || 0)
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-start font-weight-bold">
                <div className="pr-3">
                  <div>Price</div>
                  <div className="small text-secondary">
                    This price will appear as a separate line item on your
                    invoice.
                  </div>
                </div>
                <div>{formatMoney(insuranceCharge)}</div>
              </div>
            </div>
          </Col>
        </Row>
        <hr className="mx-n3 mx-md-n4" />
        <div className="text-uppercase text-secondary mb-2">
          Terms & Conditions
        </div>
        <div>
          Please review the{' '}
          <a href={rate.insurance.terms_url} target="_blank" rel="noreferrer">
            Terms & Conditions
          </a>
          .
        </div>
        <div>{rate.insurance.statement_of_fact}</div>
        <hr className="mx-n3 mx-md-n4" />
        <div className="text-uppercase text-secondary mb-2">Exclusions</div>
        {rate.insurance.exclusions?.map((exclusion, index) => {
          const item = LOADSURE.EXCLUSIONS.find((exc) => {
            return exc.value === exclusion;
          });
          if (!item) {
            if (rate.insurance.provider_type === PROVIDER_TYPE.SHIPENGINE) {
              return (
                <div className="mt-2" key={index}>
                  <div className="font-weight-bold">Uncommon Items</div>
                  <div>{defaultExclusion}</div>
                </div>
              );
            }
            return (
              <div className="mt-2" key={index}>
                <div className="font-weight-bold text-danger">
                  Unknown Exclusion
                </div>
                <div className="text-danger">
                  There was an error finding this exclusion. Please contact
                  Straightline before booking this shipment.
                </div>
              </div>
            );
          }
          return (
            <div className="mt-2" key={item.value}>
              <div className="font-weight-bold">{item.label}</div>
              <div>{item.description}</div>
            </div>
          );
        })}
        {!rate.insurance.exclusions?.length && (
          <div className="mt-2">
            <div className="font-weight-bold">Uncommon Items</div>
            <div>{defaultExclusion}</div>
          </div>
        )}
      </ModalBody>
      <InsuranceFooter setTab={setTab} />
    </div>
  );
}

function InsuranceFooter({ setTab }) {
  const [agree, setAgree] = useState(false);
  const [clicked, setClicked] = useState(false);
  const className = clicked && !agree ? 'flash' : '';

  return (
    <div className="border-top">
      <div className="p-3 px-md-4 border-bottom">
        <div className="d-flex flex-row-reverse">
          <div className={`d-flex mr-n4 animated ${className}`}>
            <div
              className="mr-3 text-secondary pointer"
              onClick={() => setAgree(!agree)}
            >
              I agree to all Terms, Conditions, & Exclusions
            </div>
            <div className="custom-control custom-control-lg custom-checkbox mr-3">
              <input
                type="checkbox"
                className="custom-control-input"
                id="agree-check"
                checked={agree}
                onChange={(e) => setAgree(!agree)}
              />
              <label className="custom-control-label" for="agree-check"></label>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between p-3 px-md-4">
        <div>
          <Button
            color="white"
            onClick={() => setTab('details')}
            className="mr-2"
          >
            Cancel
          </Button>
        </div>
        <Button
          color="primary"
          onClick={() => {
            if (!agree) {
              return setClicked(true);
            }
            setTab('book');
          }}
          // disabled={!agree}
          className={!agree ? 'disabled' : ''}
        >
          <Icon type="chevron-right" className="mr-2" />
          Continue
        </Button>
      </div>
    </div>
  );
}

function PriceOfferAlert({ offer }) {
  if (!offer) {
    return null;
  }

  if (offer.accepted_at) {
    return (
      <Alert color="primary" style={{ fontSize: '1.03rem' }}>
        Your offer has been accepted! You may now book this shipment with a{' '}
        <span className="font-weight-bold">{formatMoney(offer.amount)}</span>{' '}
        freight charge.
      </Alert>
    );
  }

  return (
    <Alert color="primary" style={{ opacity: 0.65, fontSize: '1.03rem' }}>
      Your offer of{' '}
      <span className="font-weight-bold">{formatMoney(offer.amount)}</span> is
      being reviewed!
      <br />
      You will receive a notification when it has been accepted or denied.
    </Alert>
  );
}

function Header({ quote, rate, offer }) {
  const { sm } = useScreenSize();
  const size = sm ? 'h2' : 'h3';

  return (
    <div className="modal-header py-3 px-3 px-md-4">
      <div className="w-100">
        <Row className="justify-content-between mb-n2">
          <Col className="mb-2">
            <div className={`${size} mb-0`}>
              {quote.pickup_address.city}, {quote.pickup_address.state}
              <Icon type="arrow-right" className="text-success mx-2" />
              {quote.delivery_address.city}, {quote.delivery_address.state}
            </div>
            <div className="">
              {formatDate(quote.pickup_date)}
              <Icon type="dot" className="mx-1 text-muted" />
              {formatDate(quote.due_date)}
            </div>
          </Col>
          <Col className="mb-2" sm="auto">
            <div className="text-right-sm">
              {offer && offer.accepted_at ? (
                <div className="d-flex">
                  <del
                    className={`${size} mb-0 text-primary mr-2`}
                    style={{ lineHeight: 1, opacity: 0.65 }}
                  >
                    {formatMoney(rate.price.shipper_price, {
                      precision: 0
                    })}
                  </del>
                  <div
                    className={`${size} mb-0 text-primary`}
                    style={{ lineHeight: 1 }}
                  >
                    {formatMoney(offer.amount, {
                      precision: 0
                    })}
                  </div>
                </div>
              ) : (
                <div
                  className={`${size} mb-0 text-primary`}
                  style={{ lineHeight: 1 }}
                >
                  {formatMoney(rate.price.shipper_price)}
                </div>
              )}
            </div>
            {rate.insurance && (
              <div className="text-secondary text-right small">
                extra charges*
              </div>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
}
